import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GuestSummary, GuestsResponse, SetTeamResponse, Team } from '@mv/contracts';
import { TEAMS } from '@mv/contracts';
import { IconBack, IconClose } from '../components/Icon';
import { useSession } from '../lib/session';
import Flag from '../components/Flag';
import { MarkBall, MarkWhistle } from '../components/Icon';
import { ApiError, api } from '../lib/api';
import { fmtDay, fmtTime, now, relativeUploadTime } from '../lib/time';

/**
 * The organiser's view of who is here, and the one thing they can change.
 *
 * **Read-only except for the team (D54).** D37's case against an admin console
 * is that curation surfaces at a wedding are dead code, and that still holds:
 * this is not a moderation queue, it is one control beside a read that already
 * existed. A team is an ice-breaker rather than a privilege — moving somebody
 * grants them nothing and is undone by moving them back — which is exactly the
 * line this file has always drawn around promotion to organiser. That stays a
 * deliberate act from a terminal, and nothing here does it.
 *
 * The move is confirmed rather than immediate, because the control is a list of
 * eight countries on a phone that is being passed around a table at a welcome
 * dinner, and a mis-tap that silently reassigns a guest is the failure worth
 * designing against.
 */
export default function AdminGuests() {
  const session = useSession();
  const navigate = useNavigate();
  const [data, setData] = useState<GuestsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The move an organiser has picked but not yet confirmed. */
  const [pending, setPending] = useState<{ guest: GuestSummary; team: Team } | null>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  /**
   * Kept apart from `error`, which belongs to the list load and renders in page
   * flow. A failed *move* has to render inside the sheet: the sheet stays open
   * so the organiser can retry, and it is a `position: fixed` scrim over the
   * whole viewport, so a page-level banner behind it is invisible. The first
   * version of this did exactly that and the failure test still passed, because
   * jsdom lays nothing out — the node was in the document and unreachable.
   */
  const [moveError, setMoveError] = useState<string | null>(null);

  async function commitMove() {
    if (!pending) return;
    const { guest, team } = pending;
    setSaving(true);
    setMoveError(null);
    try {
      const res = await api<SetTeamResponse>(`/admin/guests/${guest.userId}/team`, {
        method: 'POST',
        body: JSON.stringify({ team }),
      });
      // Patched in place rather than refetched. The list is sorted by last seen,
      // and a refetch here would reorder the rows under the organiser's thumb
      // the moment they confirmed — losing the row they were just looking at.
      setData((d) => d && {
        ...d,
        guests: d.guests.map((g) => (g.userId === guest.userId ? { ...g, team } : g)),
      });
      setPending(null);
      setNote(
        `${guest.displayName} is on ${team}.`
        + (res.photosReflagged > 0
          ? ` ${res.photosReflagged} ${res.photosReflagged === 1 ? 'photo' : 'photos'} re-flagged.`
          : '')
        + ' Their own screen updates when they next open their profile.',
      );
    } catch (err) {
      // `pending` is deliberately left set, so the sheet stays open with the
      // message in it and the organiser can simply press the button again.
      //
      // The API's own sentence when it sent one: the case worth distinguishing
      // is a 404 for a guest removed between the page loading and the organiser
      // confirming, where "try again" is the wrong instruction. `ApiError.message`
      // is already written for a human to read.
      setMoveError(err instanceof ApiError
        ? err.message
        : 'That did not save. Nothing changed — try again.');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    let live = true;
    api<GuestsResponse>('/admin/guests')
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setError('That did not load. Try again in a moment.'); });
    return () => { live = false; };
  }, []);

  // The API enforces this; the client only avoids rendering a screen that would
  // 403 anyway. Both checks read the same `admin` group claim.
  if (session && !session.isAdmin) {
    return (
      <div className="page page-pad empty" style={{ minHeight: '60dvh', justifyContent: 'center' }}>
        <MarkWhistle className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Not this one</h4>
        <p>That page is for organisers.</p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/me')}>Back to your profile</button>
      </div>
    );
  }

  return (
    <div className="page page-narrow page-narrow-lg" style={{ padding: '10px 20px 96px' }}>
      <button type="button" className="btn btn-secondary" style={{ minHeight: 42, gap: 8 }} onClick={() => navigate('/me')}>
        <IconBack size={16} /> Profile
      </button>

      <h3 style={{ margin: '16px 0 4px' }}>Guests</h3>
      {data && (
        <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
          {data.guests.length} signed in · {data.totalUploads} {data.totalUploads === 1 ? 'upload' : 'uploads'} between them
        </p>
      )}

      {error && <p style={{ fontSize: 13, color: 'var(--color-accent-700)', marginTop: 16 }} role="alert">{error}</p>}

      {!data && !error && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="sk sk-block" style={{ height: 62 }} />)}
        </div>
      )}

      {data && data.guests.length === 0 && (
        <div className="empty" style={{ marginTop: 24 }}>
          <MarkBall className="mark" size={44} />
          <h4 style={{ margin: 0 }}>Nobody yet</h4>
          <p>The first guest to sign in appears here.</p>
        </div>
      )}

      {data && data.guests.length > 0 && (
        <div className="stack" style={{ gap: 8, marginTop: 18 }}>
          {data.guests.map((g) => (
            <GuestRow key={g.userId} guest={g} onPick={(team) => { setNote(null); setMoveError(null); setPending({ guest: g, team }); }} />
          ))}
        </div>
      )}

      {note && (
        <p
          className="card"
          style={{ fontSize: 13, marginTop: 14, background: 'var(--color-accent-2-100)', color: 'var(--color-accent-2-900)' }}
          role="status"
        >
          {note}
        </p>
      )}

      {pending && (
        <ConfirmMove
          guest={pending.guest}
          team={pending.team}
          saving={saving}
          error={moveError}
          onCancel={() => { setMoveError(null); setPending(null); }}
          onConfirm={commitMove}
        />
      )}

      {data && data.guests.length > 0 && (
        <p style={{ fontSize: 11, opacity: 0.5, marginTop: 18 }}>
          {/* Saying "last seen" rather than "last login" is not pedantry: the
              timestamp updates on silent token refreshes too, so it tracks use
              rather than sign-in. Calling it a login would be a lie an
              organiser might act on. */}
          "Last seen" is the last time someone had the app open, not the last time they signed in.
          Sorted by it, most recent first. Times are Khao Lak time.
        </p>
      )}
    </div>
  );
}

function GuestRow({ guest, onPick }: { guest: GuestSummary; onPick: (team: Team) => void }) {
  const seen = guest.lastSeen ?? guest.registeredAt;
  const fresh = now() - seen < 36 * 3600_000;

  return (
    <div className="card guest-row">
      {/* A native `<select>` on purpose. It is the one control that gives a
          thumb a full-height wheel on iOS and a real listbox on desktop without
          this file growing a popover, keyboard handling and focus trapping of
          its own — and this screen is used one-handed, standing up. The flag
          sits beside it because the option text cannot carry an image. */}
      <label className="guest-team">
        <Flag team={guest.team} height={16} className="guest-flag" />
        <span className="sr-only">{`Team for ${guest.displayName}`}</span>
        <select
          className="guest-team-select"
          value={guest.team}
          onChange={(e) => {
            const next = e.target.value as Team;
            // `value` is bound to the guest's real team and never to the
            // pending one, so the row keeps showing what is true until the
            // move is confirmed. Cancelling therefore needs no undo.
            if (next !== guest.team) onPick(next);
          }}
        >
          {TEAMS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="guest-name">
          {guest.displayName}
          {guest.isAdmin && <span className="tag tag-accent-2" style={{ fontSize: 10 }}>Organiser</span>}
        </div>
        <div className="guest-sub">{guest.email}</div>
      </div>

      <div className="guest-meta">
        <div className="guest-uploads">
          {guest.uploads}
          <span>{guest.uploads === 1 ? 'photo' : 'photos'}</span>
        </div>
        {/* The tooltip names the timezone because the bare time does not, and
            an organiser reading this from another continent before the trip
            has no way to tell that 06:01 is Khao Lak's 06:01 and not theirs.
            Everything on the site renders in Asia/Bangkok on purpose — see
            `time.ts` — and during the week that is simply the local clock. */}
        <div className="guest-seen" title={`${fmtDay(seen)} ${fmtTime(seen)} Khao Lak time`}>
          {/* Once the week is under way, "3 min ago" is what an organiser wants.
              Before it, a relative time reads as noise, so it falls back to a
              date. */}
          {fresh ? relativeUploadTime(seen) : fmtDay(seen)}
        </div>
      </div>
    </div>
  );
}

/**
 * The confirmation step.
 *
 * Spelled out both ways round — who, from where, to where — because the control
 * that opens it is a list of eight countries on a phone being passed around a
 * table, and "are you sure?" over a mis-tap that the organiser cannot see the
 * shape of is not a confirmation, it is a speed bump.
 *
 * It also states the propagation honestly. The guest's row changes at once and
 * their photographs re-flag behind it, but their own claims are frozen until
 * their token turns over (D17), so an organiser standing next to that guest
 * needs to know what they will and will not see on the phone in their hand.
 */
function ConfirmMove({ guest, team, saving, error, onCancel, onConfirm }: {
  guest: GuestSummary; team: Team; saving: boolean; error: string | null;
  onCancel: () => void; onConfirm: () => void;
}) {
  return (
    <div className="sheet-scrim" role="dialog" aria-modal="true" aria-label="Confirm the team change">
      <div className="sheet">
        <button type="button" className="sheet-close" onClick={onCancel} aria-label="Cancel">
          <IconClose size={16} />
        </button>

        <h4 style={{ margin: '0 0 4px' }}>Move {guest.displayName}?</h4>

        <div className="move-from-to">
          <span><Flag team={guest.team} height={20} /> {guest.team}</span>
          <span aria-hidden="true" className="move-arrow">&rarr;</span>
          <span><Flag team={team} height={20} /> {team}</span>
        </div>

        <p style={{ fontSize: 13, opacity: 0.75, margin: '12px 0 0' }}>
          Their photos move to the new flag straight away. Their own screen catches up
          the next time they open their profile, and within the hour either way.
        </p>

        {/* Inside the sheet, not on the page behind it. See `moveError`. */}
        {error && (
          <p
            role="alert"
            style={{ fontSize: 13, color: 'var(--color-accent-700)', margin: '14px 0 0', fontWeight: 600 }}
          >
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={onConfirm} disabled={saving}>
            {saving ? 'Moving…' : `Move to ${team}`}
          </button>
        </div>
      </div>
    </div>
  );
}
